import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { BookingSearchData } from '../../pages/Booking'
import type { Vehicle } from '../../types/booking'
import ItineraryLocationSearchInput from '../../components/ItineraryLocationSearchInput'
import {
  isLocationInSalzburg,
  type LocationGeoData,
} from '../../utils/salzburgLocation'

export type BookingStop = {
  id: number
  location: string
  type: string
  duration: string
}

type BookingItineraryCardProps = {
  searchData: BookingSearchData
  selectedVehicle: Vehicle | null
  stops?: BookingStop[]
  extraPrice?: number
  extraPriceLabel?: string
  discount?: number
  onEditRoute?: () => void
  onSearchDataChange?: (
    updater: (
      prev: BookingSearchData
    ) => BookingSearchData
  ) => void
  arrivalInfo?: import('../../pages/Booking').ArrivalInfo
  specialRequirements?: string
  servicePrice?: number
  serviceTitle?: string
  serviceType?: string
}

function formatDisplayDate(dateStr: string) {
  if (!dateStr) return 'Add Date'

  const parts = dateStr.split('-').map(Number)

  const date =
    parts.length === 3 &&
    parts.every(
      (part) => !Number.isNaN(part)
    )
      ? new Date(
          parts[0],
          parts[1] - 1,
          parts[2]
        )
      : new Date(dateStr)

  if (Number.isNaN(date.getTime())) {
    return dateStr
  }

  const days = [
    'Sun',
    'Mon',
    'Tue',
    'Wed',
    'Thu',
    'Fri',
    'Sat',
  ]

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]

  return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]}`
}

function formatDisplayTime(timeStr: string) {
  if (!timeStr) return ''

  const [
    hoursStr,
    minutesStr = '00',
  ] = timeStr.split(':')

  const hours = Number(hoursStr)

  if (Number.isNaN(hours)) {
    return timeStr
  }

  const period = hours >= 12 ? 'PM' : 'AM'
  const hour12 = hours % 12 || 12

  return `${hour12}.${minutesStr.padStart(
    2,
    '0'
  )} ${period}`
}

function getCancellationDeadline(
  dateStr: string,
  timeStr: string
) {
  if (!dateStr || !timeStr) return ''

  const [
    year,
    month,
    day,
  ] = dateStr.split('-').map(Number)

  const [
    hours,
    minutes = 0,
  ] = timeStr.split(':').map(Number)

  if (
    ![
      year,
      month,
      day,
      hours,
      minutes,
    ].every(Number.isFinite)
  ) {
    return ''
  }

  // Subtract exactly 24 hours from the selected pickup date and time.
  // UTC is used here so the browser/device timezone does not alter the value.
  const deadline = new Date(
    Date.UTC(
      year,
      month - 1,
      day,
      hours,
      minutes
    ) -
      24 * 60 * 60 * 1000
  )

  const deadlineDate = [
    deadline.getUTCFullYear(),
    String(
      deadline.getUTCMonth() + 1
    ).padStart(2, '0'),
    String(
      deadline.getUTCDate()
    ).padStart(2, '0'),
  ].join('-')

  const deadlineTime = [
    String(
      deadline.getUTCHours()
    ).padStart(2, '0'),
    String(
      deadline.getUTCMinutes()
    ).padStart(2, '0'),
  ].join(':')

  return `${formatDisplayDate(
    deadlineDate
  )} ${formatDisplayTime(
    deadlineTime
  )}`
}

function RouteBlock({
  fromTitle,
  fromAddress,
  toTitle,
  toAddress,
  fromDate,
  stops = [],
  onEditRoute,
  onSearchDataChange,
  editingLocation,
  setEditingLocation,
  isFromRestrictedToSalzburg = false,
  isToRestrictedToSalzburg = false,
  onSelectFrom,
  onSelectTo,
  isHourly = false,
  isDayTrip = false,
  durationHours = 2,
  distanceKm = 80,
  durationText,
}: {
  fromTitle: string
  fromAddress: string
  toTitle: string
  toAddress: string
  fromDate: string
  stops?: BookingStop[]
  onEditRoute?: () => void
  onSearchDataChange?: (
    updater: (
      prev: BookingSearchData
    ) => BookingSearchData
  ) => void
  editingLocation?: 'from' | 'to' | null
  setEditingLocation?: (
    loc: 'from' | 'to' | null
  ) => void
  isFromRestrictedToSalzburg?: boolean
  isToRestrictedToSalzburg?: boolean
  onSelectFrom?: (
    location: LocationGeoData
  ) => void
  onSelectTo?: (
    location: LocationGeoData
  ) => void
  isHourly?: boolean
  isDayTrip?: boolean
  durationHours?: number
  distanceKm?: number
  durationText?: string
}) {
  return (
    <div className="mt-5 rounded-[14px] bg-[#F7F7F7] px-4 py-4">
      <div className="relative flex gap-3">
        <div className="flex w-[18px] shrink-0 flex-col items-center pt-[3px]">
          <span className="relative z-[1] h-[14px] w-[14px] rounded-full border-[3px] border-[#365E92] bg-white" />

          <span className="my-[2px] w-px flex-1 bg-[#D0D0D0]" />
        </div>

        <div className="min-w-0 flex-1 pb-4">
          <div className="flex items-start justify-between gap-3">
            <span className="font-poppins text-[10px] font-semibold uppercase tracking-[0.06em] text-[#365E92]">
              From
            </span>

            <span className="shrink-0 rounded-full bg-white px-2.5 py-[3px] font-poppins text-[10px] font-semibold text-[#444444]">
              {fromDate}
            </span>
          </div>

          {editingLocation === 'from' &&
          onSelectFrom ? (
            <ItineraryLocationSearchInput
              label="FROM"
              initialValue={fromTitle}
              placeholder="Search pickup address, hotel, or city..."
              isRestrictedToSalzburg={
                isFromRestrictedToSalzburg
              }
              onSelect={onSelectFrom}
              onCancel={() =>
                setEditingLocation?.(
                  null
                )
              }
            />
          ) : onSearchDataChange ||
            onEditRoute ? (
            <button
              type="button"
              onClick={() => {
                if (
                  onSearchDataChange &&
                  setEditingLocation
                ) {
                  setEditingLocation(
                    editingLocation ===
                      'from'
                      ? null
                      : 'from'
                  )
                } else if (
                  onEditRoute
                ) {
                  onEditRoute()
                }
              }}
              className="mt-1 w-full text-left"
            >
              <p className="font-host text-[15px] font-medium leading-[1.3] text-black">
                <strong className="font-semibold">
                  {fromTitle}
                </strong>

                {fromAddress ? (
                  <span className="font-normal text-[#555555]">
                    {`, ${fromAddress}`}
                  </span>
                ) : null}
              </p>

              <span className="mt-1 inline-block font-poppins text-[10px] font-medium text-[#365E92]">
                Edit pickup
              </span>
            </button>
          ) : (
            <p className="mt-1 font-host text-[15px] font-medium leading-[1.3] text-black">
              <strong className="font-semibold">
                {fromTitle}
              </strong>

              {fromAddress ? (
                <span className="font-normal text-[#555555]">
                  {`, ${fromAddress}`}
                </span>
              ) : null}
            </p>
          )}
        </div>
      </div>

      {stops.map(
        (stop, index) => (
          <div
            key={stop.id}
            className="relative flex gap-3"
          >
            <div className="flex w-[18px] shrink-0 flex-col items-center pt-[2px]">
              <span className="relative z-[1] flex h-[16px] w-[16px] items-center justify-center rounded-full bg-[#B8D877] font-poppins text-[9px] font-semibold text-black">
                {index + 1}
              </span>

              <span className="my-[2px] w-px flex-1 bg-[#D0D0D0]" />
            </div>

            <div className="min-w-0 flex-1 pb-4">
              <span className="font-poppins text-[10px] font-semibold uppercase tracking-[0.06em] text-[#6F9A2A]">
                Stop {index + 1}
              </span>

              <p className="mt-1 font-host text-[15px] font-medium leading-[1.3] text-black">
                {stop.location}
              </p>

              <p className="mt-0.5 font-poppins text-[10px] text-[#666666]">
                {stop.type} ·{' '}
                {stop.duration}
              </p>
            </div>
          </div>
        )
      )}

      {isHourly ? (
        <div className="relative flex gap-3">
          <div className="flex w-[18px] shrink-0 flex-col items-center pt-[3px]">
            <span className="relative z-[1] h-[14px] w-[14px] rotate-45 rounded-[3px] border-[3px] border-[#9BC344] bg-white" />
          </div>

          <div className="min-w-0 flex-1">
            <span className="font-poppins text-[10px] font-semibold uppercase tracking-[0.06em] text-[#6F9A2A]">
              Duration
            </span>

            <p className="mt-1 font-host text-[15px] font-medium leading-[1.3] text-black">
              <strong className="font-semibold">
                {durationHours} Hours
              </strong>

              <span className="font-normal text-[#555555]">
                {` (${distanceKm} km included)`}
              </span>
            </p>
          </div>
        </div>
      ) : (
        <div className="relative flex gap-3">
          <div className="flex w-[18px] shrink-0 flex-col items-center pt-[3px]">
            <span className="relative z-[1] h-[14px] w-[14px] rotate-45 rounded-[3px] border-[3px] border-[#9BC344] bg-white" />
          </div>

          <div className="min-w-0 flex-1">
            <span className="font-poppins text-[10px] font-semibold uppercase tracking-[0.06em] text-[#6F9A2A]">
              To
            </span>

            {editingLocation === 'to' &&
            onSelectTo ? (
              <ItineraryLocationSearchInput
                label="TO"
                initialValue={toTitle}
                placeholder="Search drop-off address, hotel, or city..."
                isRestrictedToSalzburg={
                  isToRestrictedToSalzburg
                }
                onSelect={onSelectTo}
                onCancel={() =>
                  setEditingLocation?.(
                    null
                  )
                }
              />
            ) : onSearchDataChange ||
              onEditRoute ? (
              <button
                type="button"
                onClick={() => {
                  if (
                    onSearchDataChange &&
                    setEditingLocation
                  ) {
                    setEditingLocation(
                      editingLocation ===
                        'to'
                        ? null
                        : 'to'
                    )
                  } else if (
                    onEditRoute
                  ) {
                    onEditRoute()
                  }
                }}
                className="mt-1 w-full text-left"
              >
                <p className="font-host text-[15px] font-medium leading-[1.3] text-black">
                  <strong className="font-semibold">
                    {toTitle}
                  </strong>

                  {toAddress ? (
                    <span className="font-normal text-[#555555]">
                      {`, ${toAddress}`}
                    </span>
                  ) : null}
                </p>

                <span className="mt-1 inline-block font-poppins text-[10px] font-medium text-[#365E92]">
                  Edit drop-off
                </span>
              </button>
            ) : (
              <p className="mt-1 font-host text-[15px] font-medium leading-[1.3] text-black">
                <strong className="font-semibold">
                  {toTitle}
                </strong>

                {toAddress ? (
                  <span className="font-normal text-[#555555]">
                    {`, ${toAddress}`}
                  </span>
                ) : null}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Temporarily hide Distance / Travel Time for Day Trips */}
      {!isDayTrip &&
        !isHourly &&
        distanceKm > 0 && (
          <div className="mt-3.5 flex items-center justify-between rounded-xl border border-[#E9ECEF] bg-white p-2.5 shadow-sm">
            <div className="flex items-center gap-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-orange/10 text-brand-orange">
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle
                    cx="6"
                    cy="19"
                    r="3"
                  />

                  <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" />

                  <circle
                    cx="18"
                    cy="5"
                    r="3"
                  />
                </svg>
              </span>

              <div>
                <p className="font-poppins text-[10px] font-medium uppercase leading-none tracking-[0.02em] text-[#71717A]">
                  Distance
                </p>

                <p className="mt-0.5 font-poppins text-[13px] font-bold text-black">
                  {distanceKm} km
                </p>
              </div>
            </div>

            <div className="h-6 w-px bg-[#E5E9EB]" />

            <div className="flex items-center gap-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#2563EB]/10 text-[#2563EB]">
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                  />

                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </span>

              <div>
                <p className="font-poppins text-[10px] font-medium uppercase leading-none tracking-[0.02em] text-[#71717A]">
                  Travel Time
                </p>

                <p className="mt-0.5 font-poppins text-[13px] font-bold text-black">
                  {durationText ||
                    (durationHours
                      ? `${durationHours} hrs`
                      : 'Estimated')}
                </p>
              </div>
            </div>
          </div>
        )}
    </div>
  )
}

export default function BookingItineraryCard({
  searchData,
  selectedVehicle,
  stops = [],
  extraPrice = 0,
  extraPriceLabel = 'Additional Options',
  discount = 0,
  onEditRoute,
  onSearchDataChange,
  arrivalInfo,
  specialRequirements,
  servicePrice = 0,
  serviceTitle,
  serviceType,
}: BookingItineraryCardProps) {
  const [searchParams] =
    useSearchParams()

  const [
    editingLocation,
    setEditingLocation,
  ] = useState<
    'from' | 'to' | null
  >(null)

  const serviceTypeParam =
    serviceType ||
    searchParams.get(
      'serviceType'
    ) ||
    ''

  const isDayTrip =
    serviceTypeParam ===
    'daytrips'

  const isHourly =
    serviceTypeParam ===
    'hourly'

  // Return journeys are supported only for normal transfers.
  const isTransfer =
    serviceTypeParam ===
    'transfers'

  const paramServicePrice =
    Number(
      searchParams.get(
        'servicePrice'
      )
    ) || 0

  const paramServiceTitle =
    searchParams.get(
      'serviceTitle'
    ) || ''

  const serviceDuration =
    searchParams.get(
      'serviceDuration'
    ) || ''

  /*
   * Hourly bookings are priced only by:
   *
   *   hourlyPrice × durationHours
   *
   * They must never inherit a servicePrice
   * from the URL or an earlier booking step.
   *
   * Day trips and all other services retain
   * the existing service-price behavior.
   */
  const resolvedServicePrice =
    isHourly
      ? 0
      : servicePrice > 0
        ? servicePrice
        : paramServicePrice

  const formatServiceType = (
    type: string
  ) => {
    if (
      type === 'privatetours'
    ) {
      return 'PRIVATE TOUR'
    }

    if (type === 'daytrips') {
      return 'DAY TRIP'
    }

    if (
      type === 'skitransfers'
    ) {
      return 'SKI TRANSFER'
    }

    if (type === 'hourly') {
      return 'HOURLY DRIVER'
    }

    if (type === 'transfers') {
      return 'TRANSFER'
    }

    return null
  }

  const servicePillLabel =
    serviceTypeParam
      ? formatServiceType(
          serviceTypeParam
        )
      : null

  const resolvedServiceTitle =
    serviceTitle ||
    paramServiceTitle ||
    (serviceTypeParam ===
    'privatetours'
      ? 'Private Tour'
      : serviceTypeParam ===
          'daytrips'
        ? 'Day Trip'
        : serviceTypeParam ===
            'skitransfers'
          ? 'Ski Transfer'
          : serviceTypeParam ===
              'hourly'
            ? 'Hourly Driver'
            : serviceTypeParam ===
                'transfers'
              ? 'Transfer'
              : 'Service')

  const durationHours =
    Number(
      searchData.durationHours
    ) > 0
      ? Number(
          searchData.durationHours
        )
      : Number(
          searchParams.get(
            'duration'
          )
        ) > 0
        ? Number(
            searchParams.get(
              'duration'
            )
          )
        : 2

  /*
   * Distance is still calculated/displayed
   * for hourly bookings because the UI shows
   * the included-km allowance.
   *
   * IMPORTANT:
   * distanceKm is NOT used in hourly pricing.
   */
  const distanceKm =
    Number(searchData.distanceKm) >
    0
      ? Number(
          searchData.distanceKm
        )
      : Number(
          searchParams.get(
            'distance'
          )
        ) > 0
        ? Number(
            searchParams.get(
              'distance'
            )
          )
        : durationHours * 40

  const pickupDateTime = `${formatDisplayDate(
    searchData.date
  )}${
    searchData.time
      ? ` ${formatDisplayTime(
          searchData.time
        )}`
      : ''
  }`

  /*
   * Day trips retain the 24-hour cancellation
   * calculation from the newer booking flow.
   *
   * Other services retain the existing
   * pickup-time cancellation display.
   */
  // const cancellationDeadline =
  //   serviceTypeParam ===
  //   'daytrips'
  //     ? getCancellationDeadline(
  //         searchData.date,
  //         searchData.time
  //       ) || pickupDateTime
  //     : pickupDateTime

  const cancellationDeadline =
    serviceTypeParam === 'privatetours'
      ? pickupDateTime
      : getCancellationDeadline(
          searchData.date,
          searchData.time
        ) || pickupDateTime

  // Mutual Salzburg boundary checks.
  const isFromInSalzburg =
    isLocationInSalzburg({
      title: searchData.fromTitle,
      address:
        searchData.fromAddress,
      lat: searchData.fromLat,
      lng: searchData.fromLng,
    })

  const isToInSalzburg =
    isLocationInSalzburg({
      title: searchData.toTitle,
      address:
        searchData.toAddress,
      lat: searchData.toLat,
      lng: searchData.toLng,
    })

  const isFromRestrictedToSalzburg =
    isHourly ||
    (!isToInSalzburg &&
      Boolean(searchData.toTitle))

  const isToRestrictedToSalzburg =
    !isFromInSalzburg &&
    Boolean(searchData.fromTitle)

  const handleSelectFrom = (
    location: LocationGeoData
  ) => {
    if (onSearchDataChange) {
      onSearchDataChange(
        (current) => ({
          ...current,
          fromTitle:
            location.title,
          fromAddress:
            location.address,
          fromLat: location.lat,
          fromLng: location.lng,
        })
      )
    }

    setEditingLocation(null)
  }

  const handleSelectTo = (
    location: LocationGeoData
  ) => {
    if (onSearchDataChange) {
      onSearchDataChange(
        (current) => ({
          ...current,
          toTitle: location.title,
          toAddress:
            location.address,
          toLat: location.lat,
          toLng: location.lng,
        })
      )
    }

    setEditingLocation(null)
  }

  const filledStops = stops.filter(
    (stop) =>
      stop.location.trim()
  )

  // TWO WAY is supported only for Transfers.
  const isTwoWay =
    isTransfer &&
    Boolean(searchData.returnDate)

  /*
   * Calculate the vehicle charge consistently
   * with the newer booking flow.
   *
   * Day Trip:
   *   No fleet vehicle charge here.
   *
   * Hourly:
   *   hourlyPrice × durationHours.
   *
   * Normal transfer/service:
   *   selectedVehicle.price is already calculated
   *   by the vehicle-selection step.
   *
   * NOTE:
   * distanceKm is deliberately NOT used here
   * for hourly bookings.
   */
  // const transportPrice =
  //   isDayTrip
  //     ? 0
  //     : isHourly
  //       ? Number(
  //           selectedVehicle
  //             ?.hourlyPrice ?? 0
  //         ) *
  //         (
  //           Number(
  //             durationHours
  //           ) > 0
  //             ? Number(
  //                 durationHours
  //               )
  //             : 1
  //         )
  //       : Number(
  //           selectedVehicle
  //             ?.price ?? 0
  //         )
  
  const dayTripVehiclePrice =
    isDayTrip && selectedVehicle
      ? Math.round(
          Number(
            (
              selectedVehicle as Vehicle & {
                dayTripPrice?: number
              }
            ).dayTripPrice ??
              selectedVehicle.price ??
              0
          )
        )
      : 0

  const transportPrice =
    isDayTrip
      ? dayTripVehiclePrice
      : isHourly
        ? Number(
            selectedVehicle?.hourlyPrice ?? 0
          ) *
          (Number(durationHours) > 0
            ? Number(durationHours)
            : 1)
        : Number(selectedVehicle?.price ?? 0)

  /*
   * Final itinerary total.
   *
   * Hourly:
   *   hourlyPrice × hours
   *   + options
   *   - discount
   *
   * Day Trip:
   *   service price
   *   + options
   *   - discount
   *
   * Other services:
   *   vehicle price
   *   + service price
   *   + options
   *   - discount
   */
  // const totalPrice =
  //   Math.max(
  //     transportPrice +
  //       resolvedServicePrice +
  //       extraPrice -
  //       discount,
  //     0
  //   )
  
  const calculatedTotalPrice =
    transportPrice +
    resolvedServicePrice +
    extraPrice -
    discount

  const totalPrice = Math.max(
    isDayTrip
      ? Math.round(calculatedTotalPrice)
      : calculatedTotalPrice,
    0
  )

  const travelerCount =
    Math.max(
      1,
      Number(
        searchData.adults || 0
      ) +
        Number(
          searchData.children || 0
        )
    )

  const luggageCount =
    Number(
      searchData.extraBags || 0
    )

  const vehicleLabel =
    selectedVehicle?.category ||
    selectedVehicle?.name ||
    'Vehicle'

  return (
    <aside className="rounded-[24px] bg-white p-4 shadow-[0_5px_20px_rgba(0,0,0,0.12)] sm:rounded-[30px] sm:p-7 lg:sticky lg:top-5 lg:self-start lg:rounded-[24px] lg:p-5">
{isDayTrip && (
  <div className="mb-4 rounded-xl border border-[#E5E7EB] bg-[#F8F9FA] p-3.5">
    <div className="flex items-start gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#E5EED5] text-[#527A22]">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="21"
          height="21"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M17 2l4 4-4 4" />
          <path d="M3 6h18" />
          <path d="M7 22l-4-4 4-4" />
          <path d="M21 18H3" />
        </svg>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-host text-[15px] font-semibold leading-snug text-black">
            Private Round-Trip Excursion
          </h3>

          <span className="inline-flex items-center gap-1 rounded-full bg-[#EAF3DF] px-2 py-1 font-poppins text-[9px] font-semibold tracking-wide text-[#527A22]">
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
            ROUND TRIP INCLUDED
          </span>
        </div>

        <p className="mt-1.5 font-poppins text-[11px] leading-[1.7] text-[#555555]">
          Travel exclusively with a private driver
          to your destination and back, for a
          seamless round-trip experience.
        </p>
      </div>
    </div>
  </div>
)}
      <div className="flex items-center justify-between">
        <h2 className="font-host text-[20px] font-semibold text-black sm:text-[23px] lg:text-[20px]">
          Summary
        </h2>

        <div className="flex items-center gap-2">
          {servicePillLabel && (
            <span className="rounded-full border border-brand-orange/20 bg-brand-orange/10 px-4 py-[7px] font-poppins text-[11px] font-semibold text-brand-orange">
              {servicePillLabel}
            </span>
          )}

          {!isHourly &&
            serviceTypeParam !==
              'daytrips' && (
              <span className="rounded-full bg-[#365E92] px-4 py-[7px] font-poppins text-[11px] font-semibold text-white">
                {isTwoWay
                  ? 'TWO WAY'
                  : 'ONE WAY'}
              </span>
            )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 rounded-full bg-[#EEEEEE] px-3 py-[7px] font-poppins text-[11px] font-medium text-black">
          <img
            src="/assets/images/image169.png"
            alt=""
            className="h-[12px] w-[12px] object-contain"
          />

          {formatDisplayDate(
            searchData.date
          )}

          {searchData.time
            ? ` · ${formatDisplayTime(
                searchData.time
              )}`
            : ''}
        </span>

        {serviceDuration && (
          <span className="flex items-center gap-1.5 rounded-full bg-[#EEEEEE] px-3 py-[7px] font-poppins text-[11px] font-medium text-black">
            <img
              src="/assets/images/icon-clock.png"
              alt=""
              className="h-[12px] w-[12px] object-contain"
            />

            {serviceDuration}
          </span>
        )}
      </div>

      <RouteBlock
        fromTitle={
          searchData.fromTitle
        }
        fromAddress={
          searchData.fromAddress
        }
        toTitle={
          searchData.toTitle
        }
        toAddress={
          searchData.toAddress
        }
        fromDate={formatDisplayDate(
          searchData.date
        )}
        stops={filledStops}
        onEditRoute={
          onEditRoute
        }
        onSearchDataChange={
          onSearchDataChange
        }
        editingLocation={
          editingLocation
        }
        setEditingLocation={
          setEditingLocation
        }
        isFromRestrictedToSalzburg={
          isFromRestrictedToSalzburg
        }
        isToRestrictedToSalzburg={
          isToRestrictedToSalzburg
        }
        onSelectFrom={
          handleSelectFrom
        }
        onSelectTo={
          handleSelectTo
        }
        isHourly={isHourly}
          isDayTrip={isDayTrip}
          durationHours={
            durationHours
          }
        distanceKm={
          distanceKm
        }
        durationText={
          searchData.durationText
        }
      />

      {isTwoWay && (
        <>
          {/* Return trip date */}
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <span className="flex min-w-0 max-w-full items-center gap-1.5 rounded-full bg-[#EEEEEE] px-3 py-[7px] font-poppins text-[11px] font-medium text-black">
              <span className="text-[18px] leading-none">
                ⇄
              </span>

              <img
                src="/assets/images/image169.png"
                alt=""
                className="h-[12px] w-[12px] shrink-0 object-contain"
              />

              <span className="truncate">
                {formatDisplayDate(
                  searchData.returnDate
                )}

                {searchData.returnTime
                  ? ` · ${formatDisplayTime(
                      searchData.returnTime
                    )}`
                  : ''}
              </span>
            </span>

            {onEditRoute && (
              <button
                type="button"
                onClick={
                  onEditRoute
                }
                className="shrink-0 font-host text-[15px] font-medium text-[#365E92]"
              >
                Edit
              </button>
            )}
          </div>

          {/* Return route - reverse of outward journey */}
          <RouteBlock
            fromTitle={
              searchData.toTitle
            }
            fromAddress={
              searchData.toAddress
            }
            toTitle={
              searchData.fromTitle
            }
            toAddress={
              searchData.fromAddress
            }
            fromDate={formatDisplayDate(
              searchData.returnDate
            )}
            durationHours={
              durationHours
            }
            distanceKm={
              distanceKm
            }
          />
        </>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-2 border-y border-[#D2D2D2] py-4">
        <span className="flex items-center gap-1 rounded-full bg-[#F2F2F2] px-3 py-[7px] font-poppins text-[10px] font-medium text-black">
          <img
            src="/assets/images/icon-adult.png"
            alt=""
            className="h-[14px] w-[14px] object-contain"
          />

          {travelerCount}
        </span>

        <span className="flex items-center gap-1 rounded-full bg-[#F2F2F2] px-3 py-[7px] font-poppins text-[11px] font-medium text-black">
          <img
            src="/assets/images/icon-child.png"
            alt=""
            className="h-[14px] w-[14px] object-contain"
          />

          {luggageCount}
        </span>

        <span className="rounded-full bg-[#EEEEEE] px-3 py-[7px] font-poppins text-[11px] font-medium text-black">
          {vehicleLabel}
        </span>
      </div>

      {(arrivalInfo?.number ||
        specialRequirements) && (
        <div className="border-b border-[#D2D2D2] py-4 lg:py-4">
          <h3 className="font-host text-[18px] font-semibold text-black lg:text-[18px]">
            Additional Details
          </h3>

          {arrivalInfo?.number && (
            <div className="mt-3">
              <p className="font-poppins text-[11px] font-semibold uppercase tracking-wider text-[#666666]">
                {arrivalInfo.type}{' '}
                Arrival
              </p>

              <div className="mt-1 flex items-center gap-2 font-host text-[14px] font-medium text-black">
                {arrivalInfo.type ===
                'flight'
                  ? '✈️'
                  : '🚆'}

                {arrivalInfo.airlineOrOperator}{' '}
                {arrivalInfo.number}

                <span className="text-[#888888]">
                  •
                </span>

                {
                  arrivalInfo.terminal
                }

                <span className="text-[#888888]">
                  •
                </span>

                {arrivalInfo.time}
              </div>
            </div>
          )}

          {specialRequirements && (
            <div className="mt-3">
              <p className="font-poppins text-[11px] font-semibold uppercase tracking-wider text-[#666666]">
                Special Requirements
              </p>

              <p className="mt-1 rounded-[8px] bg-[#F8F8F8] p-3 font-host text-[14px] font-medium italic text-black">
                "{specialRequirements}"
              </p>
            </div>
          )}
        </div>
      )}

      <div className="border-b border-[#D2D2D2] py-5 lg:py-4">
        <h3 className="font-host text-[23px] font-semibold text-black lg:text-[20px]">
          Selected Vehicle
        </h3>

        <div className="mt-3 grid grid-cols-1 items-center gap-3 sm:grid-cols-[minmax(0,1fr)_min(220px,42%)] sm:gap-4">
          <div className="min-w-0">
            <p className="font-host text-[15px] font-medium text-black">
              {selectedVehicle?.name ??
                'Select a vehicle'}
            </p>

            <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#E9E9E9] px-3 py-[7px] font-poppins text-[11px] text-black">
              <span className="h-[10px] w-[10px] rounded-full bg-[#BDBDBD]" />

              {selectedVehicle?.category ??
                '—'}
            </span>
          </div>

          <div className="flex h-[80px] w-full items-center justify-center sm:h-[95px] lg:h-[70px]">
            {selectedVehicle?.image ? (
              <img
                src={
                  selectedVehicle.image
                }
                alt={
                  selectedVehicle.name
                }
                className="max-h-[100px] w-full scale-[1.08] object-contain drop-shadow-[0_10px_10px_rgba(0,0,0,0.2)] sm:max-h-[125px] lg:max-h-[90px]"
              />
            ) : null}
          </div>
        </div>
      </div>

      <div className="hidden pt-5 lg:pt-4">
        <h3 className="font-host text-[23px] font-semibold text-black lg:text-[20px]">
          Price details
        </h3>

        {!isDayTrip && (
          <div className="mt-3 flex justify-between">
            <span className="font-host text-[15px] text-black">
              Transport (
              {selectedVehicle?.name ||
                'Fleet'}
              )
            </span>

            <span className="font-host text-[19px] text-black">
              €{transportPrice}
            </span>
          </div>
        )}

        {resolvedServicePrice >
          0 && (
          <div className="mt-3 flex justify-between">
            <span className="font-host text-[15px] text-black">
              {
                resolvedServiceTitle
              }
            </span>

            <span className="font-host text-[19px] text-black">
              €{resolvedServicePrice}
            </span>
          </div>
        )}

        {extraPrice > 0 ? (
          <div className="mt-3 flex justify-between">
            <span className="font-host text-[15px] text-black">
              {extraPriceLabel}
            </span>

            <span className="font-host text-[19px] text-black">
              €{extraPrice}
            </span>
          </div>
        ) : null}

        {discount > 0 ? (
          <div className="mt-3 flex justify-between">
            <span className="font-poppins text-[11px] text-[#65951E]">
              Promo discount
            </span>

            <span className="font-host text-[19px] text-[#65951E]">
              -€{discount}
            </span>
          </div>
        ) : null}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 rounded-[10px] bg-[#EEEEEE] px-3 py-3 sm:px-4 lg:py-2.5">
        <span className="font-host text-[20px] font-semibold text-black sm:text-[22px] lg:text-[18px]">
          Total
        </span>

        <div className="rounded-[9px] bg-[#C9E589] px-3 py-2 text-right sm:px-4 lg:px-3 lg:py-1.5">
          <span className="block font-host text-[22px] font-semibold text-black sm:text-[25px] lg:text-[20px]">
            €{totalPrice}
          </span>

          <span className="font-poppins text-[10px] text-black lg:text-[9px]">
            Reserve Now
          </span>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2.5 rounded-[12px] border border-[#CCE5CC] bg-[#F7FDF7] p-3">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="mt-0.5 flex-shrink-0 text-green-700"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>

        <span className="font-host text-[13px] font-semibold leading-normal text-green-800">
          Free cancellation until{" "}
          {cancellationDeadline}
        </span>
      </div>
    </aside>
  )
}
